import axios from "axios";
import React, { useEffect, useState } from "react";
import { Form, Modal, Button } from "react-bootstrap";
import { BASE_URL } from "../../../env";
import { FaDownload, FaUpload } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import UploadBranchManagerDocument from "./UploadBranchManagerDocument";
import { computeInsuranceAboveThreshold, calculateEmiAmountRaw } from "../../../utils/utils";

const GenerateDocumentsByBranchManagerModal = ({
  show,
  handleClose,
  handleConfirm,
  groupId,
  centerId,
  managerId,
}) => {
  const navigate = useNavigate();
  const [memberDetails, setMemberDetails] = useState(null);
  const [dbMemberDetails, setDbMemberDetails] = useState(null);
  const [
    showUploadBranchManagerDocumentModal,
    setShowUploadBranchManagerDocumentModal,
  ] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState(null);

  const dbMemberLookup = dbMemberDetails?.reduce((acc, dbmember) => {
    acc[dbmember.id] = dbmember;
    return acc;
  }, {});

  const currentISTDateTime = new Date().toISOString();

  useEffect(() => {
    const fetchMemberDetails = async () => {
      if (groupId) {
        try {
          const response = await axios.get(
            `${BASE_URL}/api/getModelData/member_details?filter={"groupId":${groupId}}&includeDepth=2&relationshipType=both&includeModels=["proposedLoanDetails","proposedLoanDetails.fk_proposed_loan_details_belongsTo_funding_agencies_fundingAgencyId"]`
          );
          setMemberDetails(response.data.data);
          setDbMemberDetails(response.data.data);
          // console.log(
          //   "members under group: " + JSON.stringify(response.data.data)
          // );
        } catch (error) {
          console.error("Error fetching members under group details:", error);
        }
      }
    };

    fetchMemberDetails();
  }, [groupId]);

  const handleClick = async () => {
    const updatedMembers = memberDetails.map((member) => {
      const loanAmount = member.proposedLoanDetails?.loanAmount || 0;
      const rateOfInterest = member.proposedLoanDetails?.rateOfInterest || 0;
      const tenureInMonths = member.proposedLoanDetails?.tenureInMonths || 0;
      const fundingAgency = member.proposedLoanDetails?.fk_proposed_loan_details_belongsTo_funding_agencies_fundingAgencyId;

      let processingCharge = 0;
      let gst = 0;
      let insuranceAmount = 0;
      let securityDeposit = 0;

      if (fundingAgency) {
        if (fundingAgency.jlgProcessingFeeEnabled) {
          processingCharge = Math.round(loanAmount * (Number(fundingAgency.jlgProcessingFeePercentage) / 100));
        }
        if (fundingAgency.jlgGstEnabled) {
          gst = Math.round(processingCharge * (Number(fundingAgency.jlgGstPercentage) / 100));
        }
        // Insurance calculation - shared with GPL and BL (bugfix.md Bug 57):
        // above Rs.2,00,000 (strictly), scaled by tenure against a 24-month
        // base, plus GST on the premium above that threshold from this
        // agency's own jlgGstPercentage. JLG previously had neither the
        // tenure scaling nor the GST step - both GPL and BL already did.
        insuranceAmount = computeInsuranceAboveThreshold(
          loanAmount,
          tenureInMonths,
          fundingAgency,
          "jlg"
        ).insurance;
        if (fundingAgency.jlgSecurityDepositEnabled) {
          securityDeposit = Math.round(calculateEmiAmountRaw(loanAmount, rateOfInterest, tenureInMonths));
        } else {
          securityDeposit = 0;
        }
      } else {
        // Fallback
        processingCharge = Math.round((loanAmount * 2.5) / 100);
        gst = Math.round((processingCharge * 18) / 100);
        securityDeposit = Math.round(calculateEmiAmountRaw(loanAmount, rateOfInterest, tenureInMonths));
        const insurancePercentage = tenureInMonths === 24 ? 2.284 : tenureInMonths === 12 ? 1.142 : 0;
        insuranceAmount = Math.round((loanAmount * insurancePercentage) / 100);
      }

      const isInsuranceAmountPaid = member.isInsuranceAmountPaid;
      const amountToRelease =
        loanAmount -
        processingCharge -
        gst -
        securityDeposit -
        (isInsuranceAmountPaid ? 0 : insuranceAmount);

      return {
        ...member,
        processingCharge,
        isProcessingChargePaid: false,
        gst,
        isGstPaid: false,
        securityDeposit,
        isSecurityDepositPaid: false,
        isLoanInsured: true,
        isInsuranceAmountPaid,
        insuranceAmount,
        amountToRelease,
      };
    });

    setMemberDetails(updatedMembers);
    try {
      // console.log("updatedMembers: " + JSON.stringify(updatedMembers));
      await axios.post(`${BASE_URL}/api/updateGenerateDocumentDetails`, {
        members: updatedMembers,
        centerId,
        bmStatusUpdatedAt: currentISTDateTime,
      });
      const response = await axios.get(
        `${BASE_URL}/api/getModelData/member_details?filter={"groupId":${groupId}}&includeDepth=1`
      );
      setMemberDetails(response.data.data);
      setDbMemberDetails(response.data.data);
    } catch (error) {
      console.log("Error updating list:", error);
    }
  };

  const handleDownload = async (memberId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/loan/formdetails/${memberId}`
      );
      const memberData = response.data?.data?.[0] || response.data || {};
      localStorage.setItem("memberDetails", JSON.stringify(memberData));

      window.open("/memberdetail/jlg/print", "_blank");
      // navigate("/memberdetail/jlg/print", {
      //   state: {
      //     memberDetails: response.data,
      //   },
      // });
    } catch (error) {
      console.log("Error fetching list:", error);
    }
  };
  const handleUpload = async (memberId) => {
    setSelectedMemberId(memberId);
    setShowUploadBranchManagerDocumentModal(true);
  };

  const handleConfirmUploadBranchManagerDocuments = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/getModelData/member_details?filter={"groupId":${groupId}}&includeDepth=1`
      );
      setMemberDetails(response.data.data);
      setDbMemberDetails(response.data.data);
      setShowUploadBranchManagerDocumentModal(false);
    } catch (error) {
      console.log("Error updating list:", error);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      dialogClassName="full-width-modal"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Confirmation</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="my-3">
          <table className="table table-bordered table-striped">
            <thead className="bg-secondary">
              <tr>
                <th>Member Name</th>
                <th>Mark</th>
                <th>Proposed Loan Amount</th>
                <th>Processing Charge (2.5 % loan amount)</th>
                <th>GST (18 % processing charge)</th>
                {(!memberDetails?.[0]?.proposedLoanDetails?.fk_proposed_loan_details_belongsTo_funding_agencies_fundingAgencyId || memberDetails?.[0]?.proposedLoanDetails?.fk_proposed_loan_details_belongsTo_funding_agencies_fundingAgencyId?.jlgSecurityDepositEnabled !== false) && (
                  <th>Security Deposit (1 month emi amount)</th>
                )}
                {(!memberDetails?.[0]?.proposedLoanDetails?.fk_proposed_loan_details_belongsTo_funding_agencies_fundingAgencyId || (memberDetails?.[0]?.proposedLoanDetails?.fk_proposed_loan_details_belongsTo_funding_agencies_fundingAgencyId?.jlgInsuranceEnabled !== false || (memberDetails?.[0]?.proposedLoanDetails?.loanAmount > 200000 && memberDetails?.[0]?.proposedLoanDetails?.fk_proposed_loan_details_belongsTo_funding_agencies_fundingAgencyId?.jlgInsuranceAbove199999Enabled !== false))) && (
                  <>
                    <th>Is Insurance Collected</th>
                    <th>Insurance Amount</th>
                  </>
                )}
                <th>Loan Amount To Release</th>
                <th>Download/Upload</th>
                <th>Uploaded file names</th>
              </tr>
            </thead>
            <tbody>
              {memberDetails?.map((member) => {
                const loanAmount = member.proposedLoanDetails?.loanAmount || 0;
                const rateOfInterest =
                  member.proposedLoanDetails?.rateOfInterest || 0;
                const tenureInMonths =
                  member.proposedLoanDetails?.tenureInMonths || 0;
                const fundingAgency = member.proposedLoanDetails?.fk_proposed_loan_details_belongsTo_funding_agencies_fundingAgencyId;
                let processingCharge = 0;
                let gst = 0;
                let insuranceAmount = 0;
                let securityDeposit = 0;

                if (fundingAgency) {
                  if (fundingAgency.jlgProcessingFeeEnabled) {
                    processingCharge = Math.round(loanAmount * (Number(fundingAgency.jlgProcessingFeePercentage) / 100));
                  }
                  if (fundingAgency.jlgGstEnabled) {
                    gst = Math.round(processingCharge * (Number(fundingAgency.jlgGstPercentage) / 100));
                  }
                  // Same shared rule as handleClick above - see its comment.
                  insuranceAmount = computeInsuranceAboveThreshold(
                    loanAmount,
                    tenureInMonths,
                    fundingAgency,
                    "jlg"
                  ).insurance;
                  if (fundingAgency.jlgSecurityDepositEnabled) {
                    securityDeposit = Math.round(calculateEmiAmountRaw(loanAmount, rateOfInterest, tenureInMonths));
                  } else {
                    securityDeposit = 0;
                  }
                } else {
                  processingCharge = Math.round((loanAmount * 2) / 100);
                  gst = Math.round((processingCharge * 18) / 100);
                  securityDeposit = Math.round(calculateEmiAmountRaw(loanAmount, rateOfInterest, tenureInMonths));
                  const insurancePercentage = tenureInMonths === 24 ? 2.284 : tenureInMonths === 12 ? 1.142 : 0;
                  insuranceAmount = Math.round((loanAmount * insurancePercentage) / 100);
                }

                const amountToRelease =
                  loanAmount -
                  (member.processingCharge || processingCharge) -
                  (member.gst || gst) -
                  (member.securityDeposit || securityDeposit) -
                  (member.isInsuranceAmountPaid ? 0 : (member.insuranceAmount || insuranceAmount));
                const validDbMembersCount = dbMemberDetails?.filter(
                  (dbmember) =>
                    dbmember.isAttendedBank && dbmember.processingCharge
                ).length;
                const validMembersCount = memberDetails?.filter(
                  (dbmember) =>
                    dbmember.isAttendedBank && dbmember.processingCharge
                ).length;

                return (
                  <tr key={member.id}>
                    <td>{member.memberName}</td>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={!!member.isAttendedBank}
                        onChange={(e) => {
                          const updatedMembers = memberDetails.map((m) =>
                            m.id === member.id
                              ? { ...m, isAttendedBank: e.target.checked }
                              : m
                          );
                          setMemberDetails(updatedMembers);
                        }}
                        disabled={member.branchManagerStatus === "disbursed"}
                      />
                    </td>
                    <td>{loanAmount}</td>
                    <td>{member.processingCharge || processingCharge}</td>
                    <td>{member.gst || gst}</td>
                    {(!fundingAgency || fundingAgency.jlgSecurityDepositEnabled !== false) && (
                      <td>{member.securityDeposit || securityDeposit}</td>
                    )}
                    {(!fundingAgency || (fundingAgency.jlgInsuranceEnabled !== false || (loanAmount > 200000 && fundingAgency.jlgInsuranceAbove199999Enabled !== false))) && (
                      <>
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={!!member.isInsuranceAmountPaid}
                            onChange={(e) => {
                              const updatedMembers = memberDetails.map((m) =>
                                m.id === member.id
                                  ? {
                                    ...m,
                                    isInsuranceAmountPaid: e.target.checked,
                                  }
                                  : m
                              );
                              setMemberDetails(updatedMembers);
                            }}
                            disabled={member.branchManagerStatus === "disbursed"}
                          />
                        </td>
                        <td>{member.insuranceAmount || insuranceAmount}</td>
                      </>
                    )}
                    <td>{member.amountToRelease || amountToRelease}</td>
                    <td>
                      {member.branchManagerStatus !== "disbursed" &&
                        dbMemberLookup[member.id]?.isAttendedBank &&
                        dbMemberLookup[member.id]?.processingCharge &&
                        member.isAttendedBank &&
                        member.processingCharge &&
                        validMembersCount >= 4 &&
                        validMembersCount <= 6 &&
                        validDbMembersCount >= 4 &&
                        validDbMembersCount <= 6 ? (
                        <>
                          <Button
                            variant="primary"
                            onClick={() => handleDownload(member.id)}
                            className="mx-3"
                          >
                            <FaDownload />
                          </Button>
                          <Button
                            variant="primary"
                            onClick={() => handleUpload(member.id)}
                            className="mx-3"
                          >
                            <FaUpload />
                          </Button>
                        </>
                      ) : (
                        <span className="text-muted">Not available</span>
                      )}
                    </td>
                    <td>
                      {member.branchManagerVerificationPhotosDetails?.map(
                        (photo, index) => (
                          <React.Fragment key={index}>
                            <a
                              href={`${BASE_URL}/images/${photo.fileName}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {photo.photoName}
                            </a>
                            {index !==
                              member.branchManagerVerificationPhotosDetails
                                .length -
                              1 && ", "}
                          </React.Fragment>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Button variant="primary" onClick={handleClick}>
            Generate Documents to Download
          </Button>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={async () => {
            await handleClick();
            await handleConfirm();
          }}
        >
          Ok
        </Button>
      </Modal.Footer>
      {showUploadBranchManagerDocumentModal && (
        <UploadBranchManagerDocument
          show={showUploadBranchManagerDocumentModal}
          handleClose={() => setShowUploadBranchManagerDocumentModal(false)}
          handleConfirm={handleConfirmUploadBranchManagerDocuments}
          memberId={selectedMemberId}
          branchManagerId={managerId}
        />
      )}
    </Modal>
  );
};

export default GenerateDocumentsByBranchManagerModal;


